// Version: 2.55
// v2.55: Phone width. The selected node's panel takes the screen's width (it took the
//        width of its widest row, 508px on a 390px phone, so every card, button row and
//        tab was cut off on the right), and the tabs wrap as a row instead of a two-column
//        grid with mismatched underlines. The Reboot, Restart, Pull and Run All row wraps. Container image names wrap
//        instead of being cut short.
// v2.54: Edge images show newer versions (app.py v4.45): each row names the newest newer
//        version of its tag, and Change version lists them (patch, minor, major, newest
//        first, read fresh from the registry) to pick from; picking one checks it. The
//        "also change" tick box sits beside its text (the shared label style made it a
//        full-width uppercase heading).
// v2.53: Change version on every Edge images row (app.py v4.44): type the new tag, Check looks
//        it up on the registry and shows what will change (a pinned image stays pinned, to the
//        new tag's current build; CIAS backend and frontend change together unless unticked),
//        then Change version or Change version and roll out. Recent moves show version changes.
// v2.52: Edge images card above the node list (app.py v4.42): every image in edge-dist's
//        compose.yml, which pinned builds have a newer build of the same tag, Manual or
//        Automatic per image, Move pin, Move pin and roll out, Roll out to all nodes (the
//        first node, then the rest), the roll-out's progress per node with Stop, the days
//        before an automatic move, which node goes first, and recent moves not yet in the
//        repo.
// v2.51: Container images pinned to a build in compose.yml show Pinned (grey), with "Newer
//        build of this tag published" when the tag has moved on (edge-node-check v1.16). A
//        pinned image showed Newer image for ever, as Pull newer images rightly keeps the pin.
// v2.50: "Update available" now only ever means the NAS's own software (DSM). Container
//        images newer than the ones running read Newer image, scripts older than Core's
//        read Behind Core, the button reads Pull newer images, and the NAS System row is
//        DSM: Up to date or Update available (version), with when and how it was checked
//        (edge-node-check v1.15).
// v2.49: Each node shows the private address of its NAS (nas_ip, else the heartbeat's
//        lan_ip, app.py v4.39): a NAS IP block beside Edge ID, with a copy button, and
//        in the node list. When the edge reports a different LAN address it is shown too.
// v2.48: Node status strip: Next Ping, Edge ID and QuickConnect sit on the Online row; the buttons get their own full-width row, so the Linux desktop buttons, login and notes add a line below instead of pushing the numbers around.
// v2.47: Linux desktop version note says it is set in the edge compose.yml (app.py v4.35), not WETO_IMAGE_TAG.
// v2.46: The demo edge node's Open Desktop shows a picture of the Linux desktop (weto.demo_url, app.py v4.30).
// v2.45: Removed an eslint comment naming react-hooks/exhaustive-deps, a rule this build does not have (Failed to compile).
// v2.44: Start Desktop opens the desktop's window straight away with a "Starting desktop" page and switches it to
//        the desktop once the node reports it running; Stop Desktop, an auto-stop or a failed start closes it.
//        Only windows this page opened can be controlled (a reload loses them; the desktop then just disconnects).
// v2.43: Open Desktop opens the desktop in its own browser window (one per node, reused if already open), not a tab.
// v2.42: A desktop request the node has already answered (request.done, app.py v4.28) no longer shows as Starting or Stopping.
// v2.41: Schedule tab shows the Linux desktop version read-only: it is set for every node by WETO_IMAGE_TAG in Clearview's .env (app.py v4.27).
// v2.40: Desktop Start and Stop reach the node within about 15 seconds (edge desktop poll, app.py v4.26), so
//        the controls refresh every 5 seconds while the desktop is starting, stopping or downloading, and the
//        wording no longer says the next check-in.
// v2.39: LinuxDesktopControls is exported for Client Management (compact: Open is labelled Desktop); every
//        button is type="button" so it never submits a surrounding form.
// v2.38: Linux desktop: Open Desktop passes the per-start login (Clearview generates it) and the login is shown
//        with a copy button; the pinned desktop image's state is shown (downloads overnight, Download Now);
//        not set up until the node has compose.yml v1.48 and its weto tunnel token. Schedule tab gains
//        Linux Desktop Version (image tag).
// v2.37: On-demand Linux desktop (weto) beside Web Terminal: Start Desktop, then Open Desktop and Stop Desktop
//        once the edge reports it running, with who started it and when it auto-stops. Start and Stop go
//        through the heartbeat action queue (weto_start, weto_stop), so they take effect at the next check-in.
//        Schedule tab gains Linux Desktop Auto-Stop (30 minutes to 8 hours, or Never; default 4 hours).
// v2.36: Number tiles use the shared .stat-card; inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v2.35: Compliance Portal, Web Terminal, NAS Console and Go to Portal links use the shared button classes; the Online status is in the normal font.
// v2.34: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v2.33: ?node=<edge id>&tab=<tab> opens that node and tab (links from System
//        Admin's Edge scripts row).
// v2.32: Network Labels site name shown beside each edge node — as a chip on
//        the header line next to the client, and as a short line under the
//        short ID in the node list. Unnamed sites show the subnet in grey with
//        "unnamed site". Uses the new sites array from GET /api/edge/nodes.
import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from './auth';

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

// ── Site name (from Network Labels) ──────────────────────────────────────────

function SiteChips({ sites }) {
  if (!Array.isArray(sites) || sites.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {sites.map(s => (
        <span
          key={s.subnet}
          title={s.named ? `${s.label} (${s.subnet})` : `${s.subnet} has no site name yet. Name it in Client Management, Network Labels.`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
            border: `1px solid ${s.named ? 'rgba(0,212,255,0.35)' : 'var(--border)'}`,
            background: s.named ? 'rgba(0,212,255,0.08)' : 'transparent',
            color: s.named ? 'var(--accent)' : 'var(--text-muted)',
            whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          📍 {s.named ? s.label : (
            <span><span style={{ fontFamily: 'var(--font-mono)' }}>{s.subnet}</span> · unnamed site</span>
          )}
        </span>
      ))}
    </span>
  );
}

function siteSummary(sites) {
  if (!Array.isArray(sites) || sites.length === 0) return null;
  const named = sites.filter(s => s.named);
  if (named.length > 0) {
    return { text: named.map(s => s.label).join(', '), named: true };
  }
  return { text: sites.map(s => s.subnet).join(', '), named: false };
}

// ── Time helpers ──────────────────────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return 'Never';
  const secs = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (secs < 60)    return `${secs}s ago`;
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function countdownTo(iso, intervalSecs) {
  if (!iso) return null;
  const elapsed   = (Date.now() - new Date(iso)) / 1000;
  const remaining = Math.max(0, intervalSecs - elapsed);
  if (remaining === 0) return 'overdue';
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = Math.floor(remaining % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function fmtDt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT';
}

function nextRunColor(iso, intervalSecs) {
  if (!iso) return '#86868b';
  const elapsed   = (Date.now() - new Date(iso)) / 1000;
  const remaining = Math.max(0, intervalSecs - elapsed);
  const pct       = remaining / intervalSecs;
  if (pct > 0.25) return '#30d158';
  if (pct > 0.05) return '#e6b800';
  return '#ff2d55';
}

// ── Node status ───────────────────────────────────────────────────────────────

function heartbeatStatus(node) {
  const ts = node.last_heartbeat || node.last_seen;
  if (!ts) return 'unknown';
  const secs         = (Date.now() - new Date(ts)) / 1000;
  const intervalSecs = (node.config?.heartbeat_interval_mins || 15) * 60;
  if (secs > intervalSecs * 2)    return 'offline';
  if (secs > intervalSecs * 1.25) return 'late';
  return 'online';
}

const CONNECTIVITY_COLORS = { online: '#30d158', late: '#e6b800', offline: '#ff2d55', unknown: '#86868b' };
const CONNECTIVITY_LABELS = { online: 'Online', late: 'Late', offline: 'Offline', unknown: 'Unknown' };

function nodeStatus(node) {
  const cs = heartbeatStatus(node);
  if (cs === 'offline' || cs === 'unknown') return cs;
  const health = node.health?.overall;
  if (health === 'error' || health === 'warning') return 'degraded';
  return cs;
}

const STATUS_COLORS = { online: '#30d158', late: '#e6b800', degraded: '#e6b800', offline: '#ff2d55', unknown: '#86868b' };
const HEALTH_COLORS = { ok: '#30d158', warning: '#e6b800', error: '#ff2d55', unknown: '#86868b' };
const HEALTH_LABELS = { ok: 'OK', warning: 'Warning', error: 'Issue', unknown: 'Unknown' };

const SCAN_LABELS = {
  rustscan:         'Network Scan',
  snmp:             'Device Details',
  ciso:             'Compliance Sync',
  backup:           'Backup Check',
  node_health:      'Node Health Check',
  all:              'All Scans',
  restart_services: 'Service Restart',
  reboot_node:      'NAS Reboot',
  check_scripts:    'Script Update Check',
  update_images:    'Pull Newer Images',
  weto_start:       'Linux Desktop Start',
  weto_stop:        'Linux Desktop Stop',
  weto_pull:        'Linux Desktop Download',
};

const WETO_DEFAULT_TAG = '3d3463cb-ls321';

// ── On-demand Linux desktop (weto) ────────────────────────────────────────────

function fmtGmt(d) {
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT';
}

function wetoAutoStopLabel(mins) {
  if (!mins) return 'Never';
  return mins < 60 ? `${mins} minutes` : mins === 60 ? '1 hour' : `${mins / 60} hours`;
}

// The edge reports state on each heartbeat; actions are picked up on the next one.
// A request counts as in flight until the edge's report shows it done, or it is old.
function wetoState(node) {
  const w       = node.weto || {};
  // A request the node has already answered (done) is not in flight
  const req     = w.request && !w.request.done ? w.request : null;
  const hbSecs  = (node.config?.heartbeat_interval_mins || 15) * 60;
  const reqAge  = req?.at ? (Date.now() - new Date(req.at)) / 1000 : Infinity;
  const stopAt  = w.last_stop?.at ? new Date(w.last_stop.at).getTime() : 0;
  const reqTime = req?.at ? new Date(req.at).getTime() : 0;
  // An error only counts if the edge hit it after the latest request
  const errAt   = w.last_error_at ? new Date(w.last_error_at).getTime() : 0;
  const failed  = !!w.last_error && errAt >= reqTime;
  if (w.running) {
    if (req?.action === 'weto_stop' && reqAge < 2 * hbSecs + 120) return 'stopping';
    return 'running';
  }
  if (w.starting) return 'starting';
  // Start in flight: allow two check-ins plus 15 minutes for the first image download
  if (req?.action === 'weto_start' && reqAge < 2 * hbSecs + 900 && stopAt < reqTime && !failed) return 'starting';
  if (!w.available || !w.tunnel_configured) return 'unavailable';
  return 'stopped';
}

// Desktop windows this page opened, by short edge id: { win, navigated }. Shared by Edge Nodes and Client
// Management. Browsers only let a page open a window from a click, and close windows it opened itself.
const desktopWindows = {};

function desktopWindowFeatures() {
  return `popup=yes,width=${Math.min(1600, window.screen.availWidth)},height=${Math.min(1000, window.screen.availHeight)}`;
}

function openStartingWindow(shortId, name) {
  const win = window.open('', `stg-desktop-${shortId}`, desktopWindowFeatures());
  if (!win) return;   // pop-ups blocked: Open Desktop still works by hand
  try {
    const doc = win.document;
    doc.title = 'Starting desktop';
    doc.body.style.cssText = 'margin:0;background:#0b1220;color:#e5e7eb;font:15px system-ui,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;text-align:center';
    doc.body.innerHTML = '';
    const msg = doc.createElement('div');
    msg.textContent = `Starting the desktop on ${name}. This window opens it when it is ready, usually within a minute.`;
    msg.style.maxWidth = '420px';
    doc.body.appendChild(msg);
  } catch { /* window already showing the desktop (another origin): leave it */ }
  desktopWindows[shortId] = { win, navigated: false };
}

function closeDesktopWindow(shortId, fromClick = false) {
  let entry = desktopWindows[shortId];
  if (!entry && fromClick) {
    // After a reload the reference is gone; from a click the named window can be found again.
    // If none was open this briefly opens an empty one, which is closed at once.
    const win = window.open('', `stg-desktop-${shortId}`, desktopWindowFeatures());
    if (win) entry = { win };
  }
  if (entry?.win && !entry.win.closed) entry.win.close();
  delete desktopWindows[shortId];
}

export function LinuxDesktopControls({ node, onRefresh, compact = false }) {
  const [busy, setBusy]     = React.useState(false);
  const [error, setError]   = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const w       = node.weto || {};
  const state   = wetoState(node);
  const shortId = node.edge_id.substring(0, 8);
  const name    = node.label || shortId;
  const login   = w.credentials || null;
  const hbSecs  = (node.config?.heartbeat_interval_mins || 15) * 60;
  const pullAge = w.pull_request?.at ? (Date.now() - new Date(w.pull_request.at)) / 1000 : Infinity;
  const pullQueued = !w.pulling && !w.image_ready && pullAge < 2 * hbSecs + 120;
  const tag     = w.image_tag || node.config?.weto_image_tag || WETO_DEFAULT_TAG;
  const changing = state === 'starting' || state === 'stopping' || pullQueued;

  // While something is changing, refresh quickly: the node reports within about 15 seconds
  const refreshRef = React.useRef(onRefresh);
  refreshRef.current = onRefresh;
  React.useEffect(() => {
    if (!changing) return;
    const t = setInterval(() => { if (refreshRef.current) refreshRef.current(); }, 5000);
    return () => clearInterval(t);
  }, [changing]);

  const send = async (action) => {
    if (action === 'weto_stop' && !window.confirm(`Stop the Linux desktop on ${name}? Anyone using it will be disconnected.`)) return;
    if (action === 'weto_start' && w.reported_at && !w.image_ready && !window.confirm(
      `The desktop version ${tag} is not downloaded on ${name} yet, so starting now downloads it over the client's internet connection (over 1 GB). Start anyway?`)) return;
    // Window work happens here, inside the click, so pop-up blockers allow it
    if (action === 'weto_start') openStartingWindow(shortId, name);
    if (action === 'weto_stop') closeDesktopWindow(shortId, true);
    setBusy(true);
    setError('');
    try {
      const r = await authFetch(`/api/edge/nodes/${node.edge_id}/run`, { method: 'POST', body: JSON.stringify({ scan: action }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      if (onRefresh) onRefresh();
    } catch (e) {
      if (action === 'weto_start') closeDesktopWindow(shortId);
      setError(e.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setBusy(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(login.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { setError('Could not copy the password'); setTimeout(() => setError(''), 4000); }
  };

  // Open with the login in the address so the browser answers the login prompt itself.
  // If Cloudflare Access sends the browser to sign in first, the prompt appears: use Copy password.
  // The demo node has no real desktop: it opens a picture of one instead
  const openUrl = w.demo_url ? w.demo_url : login
    ? `https://${encodeURIComponent(login.user)}:${encodeURIComponent(login.password)}@weto-${shortId}.stg.limited/`
    : `https://weto-${shortId}.stg.limited/`;

  // Own window per node, sized for a desktop; the same window is reused if it is already open
  const openDesktop = (e) => {
    const win = window.open(openUrl, `stg-desktop-${shortId}`, desktopWindowFeatures());
    if (win) {   // blocked pop-up: the link opens a tab instead
      e.preventDefault();
      win.focus();
      desktopWindows[shortId] = { win, navigated: true };
    }
  };

  // Follow the desktop's state in the window this page opened: show the desktop once it is running,
  // close the window once it has stopped (Stop Desktop, auto-stop or a failed start)
  React.useEffect(() => {
    const entry = desktopWindows[shortId];
    if (!entry) return;
    if (entry.win.closed) { delete desktopWindows[shortId]; return; }
    if (state === 'running' && !entry.navigated && login) {
      entry.win.location.href = openUrl;
      entry.navigated = true;
    } else if (state === 'stopped' || state === 'unavailable') {
      closeDesktopWindow(shortId);
    }
  }, [state]);

  const startedBy = w.request?.action === 'weto_start' ? w.request.by : null;
  let detail = null;
  if (state === 'running' && w.started_at) {
    const started = new Date(w.started_at);
    const stopMsg = w.auto_stop_mins
      ? `auto-stops ${fmtGmt(new Date(started.getTime() + w.auto_stop_mins * 60000))}`
      : 'no auto-stop';
    detail = `Desktop started ${fmtGmt(started)}${startedBy ? ` by ${startedBy}` : ''}, ${stopMsg}`;
  } else if (state === 'starting') {
    detail = w.starting ? 'Desktop is starting, usually under a minute' : 'Sending the start to the node';
  } else if (state === 'stopping') {
    detail = 'Desktop is stopping';
  } else if (state === 'unavailable' && w.reported_at) {
    detail = !w.available
      ? 'Linux desktop is not set up on this node yet (needs compose.yml v1.48)'
      : 'Linux desktop is not set up on this node yet (needs CLOUDFLARED_TOKEN_WETO in .env)';
  } else if (state === 'stopped' && w.reported_at) {
    if (w.pulling)            detail = `Downloading desktop version ${w.pulling}`;
    else if (pullQueued)      detail = `Desktop version ${tag} download is starting`;
    else if (w.pull_error)    detail = null;
    else if (!w.image_ready)  detail = `Desktop version ${tag} is not downloaded yet; it downloads overnight (20:00 to 06:00)`;
  }

  const startFailed = state === 'stopped' && !!w.last_error;
  const pullFailed  = state === 'stopped' && !w.image_ready && !w.pulling && !!w.pull_error;
  const note        = error ? `Error: ${error}`
                    : startFailed ? `Desktop failed to start: ${w.last_error}`
                    : pullFailed  ? `Desktop version ${tag} failed to download: ${w.pull_error}`
                    : detail;
  const noteIsError = !!error || startFailed || pullFailed;
  const canDownload = state === 'stopped' && w.reported_at && !w.image_ready && !w.pulling && !pullQueued;

  return (
    <>
      {state === 'running' && (
        <a href={openUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" onClick={openDesktop}>
          {compact ? 'Desktop ↗' : 'Open Desktop ↗'}
        </a>
      )}
      {(state === 'running' || state === 'stopping') && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => send('weto_stop')} disabled={busy || state === 'stopping'}>
          {state === 'stopping' ? 'Stopping Desktop' : 'Stop Desktop'}
        </button>
      )}
      {(state === 'stopped' || state === 'starting' || state === 'unavailable') && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => send('weto_start')}
          disabled={busy || state !== 'stopped'}
          title={state === 'unavailable' ? 'Not set up on this node yet' : `Auto-stops after ${wetoAutoStopLabel(node.config?.weto_auto_stop_mins ?? 240)}`}>
          {state === 'starting' ? 'Starting Desktop' : 'Start Desktop'}
        </button>
      )}
      {canDownload && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => send('weto_pull')} disabled={busy}
          title="Download the desktop now instead of overnight; uses the client's internet connection">
          Download Now
        </button>
      )}
      {(state === 'running' || state === 'starting') && login && (
        <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-muted)' }}>
          <span>Desktop login: <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{login.user}</span></span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={copyPassword}>{copied ? 'Copied' : 'Copy password'}</button>
          <span>New login on every start</span>
        </div>
      )}
      {note && (
        <div style={{ flexBasis: '100%', fontSize: 11, color: noteIsError ? '#ff2d55' : 'var(--text-muted)' }}>
          {note}
        </div>
      )}
    </>
  );
}

// ── Scan rows ─────────────────────────────────────────────────────────────────

function getScanRows(node) {
  const cfg     = node.config || {};
  const history = node.scan_history || {};
  const cisHrs  = cfg.ciso_push_interval_hours || 4;
  const rsMins  = cfg.rustscan_interval_hours  || 4;
  return [
    { key: 'rustscan', label: 'Network Scan',     intervalSecs: rsMins * 3600,  lastRun: history.rustscan || null, description: 'Internal network device discovery' },
    { key: 'ciso',     label: 'Compliance Sync',  intervalSecs: cisHrs * 3600,  lastRun: history.ciso     || null, description: 'Compliance framework data push' },
    { key: 'backup',      label: 'Backup Check',     intervalSecs: 24 * 3600,     lastRun: history.backup      || null, description: 'NAS and Microsoft 365 backup status' },
    { key: 'snmp',        label: 'Device Details',   intervalSecs: rsMins * 3600, lastRun: history.snmp        || null, description: 'SNMP device inventory and details' },
    { key: 'node_health', label: 'Node Health',      intervalSecs: 24 * 3600,     lastRun: history.node_health || null, description: 'NAS security and health baseline check' },
  ];
}

// ── Shared components ─────────────────────────────────────────────────────────

function StatusDot({ color, pulse }) {
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: color, boxShadow: pulse ? `0 0 6px ${color}` : 'none', flexShrink: 0,
    }} />
  );
}

function HealthCard({ component }) {
  const color = HEALTH_COLORS[component.status] || '#86868b';
  const label = HEALTH_LABELS[component.status] || component.status;
  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--border)',
      borderLeft: `3px solid ${color}`, borderRadius: 8,
      padding: '12px 16px', display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', gap: 12,
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>{component.label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{component.message}</div>
        {fmtDt(component.last_run) && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            Updated {fmtDt(component.last_run)}
          </div>
        )}
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
        background: `${color}20`, border: `1px solid ${color}60`, color,
        whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)',
      }}>{label}</span>
    </div>
  );
}

// ── Inline label editor ───────────────────────────────────────────────────────

function NodeLabelEditor({ node, onRenamed }) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue]     = React.useState(node.label || '');
  const [saving, setSaving]   = React.useState(false);
  const [error, setError]     = React.useState('');
  const inputRef              = React.useRef(null);

  React.useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  // Keep value in sync if node label changes externally
  React.useEffect(() => {
    if (!editing) setValue(node.label || '');
  }, [node.label, editing]);

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) { setError('Name cannot be empty'); return; }
    if (trimmed === (node.label || '')) { setEditing(false); return; }
    setSaving(true); setError('');
    try {
      const res = await authFetch(`/api/edge/nodes/${node.edge_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ label: trimmed }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Save failed');
      onRenamed(node.edge_id, trimmed);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setValue(node.label || ''); setEditing(false); setError(''); };

  const handleKey = (e) => {
    if (e.key === 'Enter')  save();
    if (e.key === 'Escape') cancel();
  };

  if (!editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, maxWidth: '100%', flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 'clamp(15px, 4.5vw, 18px)', fontWeight: 800, color: 'var(--text)',
          minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word', lineHeight: 1.25,
        }}>
          {node.label || node.client_slug}
        </span>
        <button className="btn btn-quiet btn-sm btn-icon" style={{ flexShrink: 0 }}
          onClick={() => { setValue(node.label || ''); setEditing(true); }}
          title="Rename this node"
         
        >
          ✎
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input className="input-sm"
          ref={inputRef}
          value={value}
          onChange={e => { setValue(e.target.value); setError(''); }}
          onKeyDown={handleKey}
          maxLength={80}
          style={{ fontWeight: 700, width: 260 }}
        />
        <button className="btn btn-primary btn-sm"
          onClick={save}
          disabled={saving}
         
        >
          {saving ? '…' : 'Save'}
        </button>
        <button className="btn btn-secondary btn-sm"
          onClick={cancel}
         
        >
          Cancel
        </button>
      </div>
      {error && <div style={{ fontSize: 11, color: '#ff2d55' }}>{error}</div>}
    </div>
  );
}


// ── Inline QuickConnect ID editor ────────────────────────────────────────────

function QuickConnectEditor({ node, onUpdated }) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue]     = React.useState(node.quickconnect_id || '');
  const [error, setError]     = React.useState('');
  const inputRef              = React.useRef(null);

  React.useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  React.useEffect(() => {
    if (!editing) setValue(node.quickconnect_id || '');
  }, [node.quickconnect_id, editing]);

  const save = async () => {
    const stripped = value.trim().replace(/^https?:\/\/quickconnect\.to\//i, '');
    const trimmed = stripped;
    if (trimmed === (node.quickconnect_id || '')) { setEditing(false); return; }
    setError('');
    try {
      const res = await authFetch(`/api/edge/nodes/${node.edge_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ quickconnect_id: trimmed }),
      });
      if (!res.ok) { const d = await res.json(); setError(d.error || 'Update failed'); return; }
      onUpdated();
      setEditing(false);
    } catch (e) {
      setError('Network error');
    }
  };

  const cancel = () => { setValue(node.quickconnect_id || ''); setEditing(false); setError(''); };

  const onKey = e => {
    if (e.key === 'Enter')  save();
    if (e.key === 'Escape') cancel();
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input className="input-sm"
            ref={inputRef}
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={onKey}
            onBlur={save}
            placeholder="ID only — e.g. STGKBCORE"
            style={{ width: 140 }}
          />
          <button className="btn btn-quiet btn-sm btn-icon" onClick={cancel}
           >
            ✕
          </button>
        </div>
        {error && <div style={{ fontSize: 11, color: '#ff2d55' }}>{error}</div>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {node.quickconnect_id ? (
        <a href={`https://quickconnect.to/${node.quickconnect_id}`}
          target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)',
            fontFamily: 'var(--font-mono)', lineHeight: 1, textDecoration: 'none' }}>
          {node.quickconnect_id}
        </a>
      ) : (
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)',
          fontFamily: 'var(--font-mono)', lineHeight: 1 }}>—</span>
      )}
      <span title="Edit QuickConnect ID"
        onClick={() => { setValue(node.quickconnect_id || ''); setEditing(true); }}
        style={{ fontSize: 10, color: 'var(--text-muted)', cursor: 'pointer' }}>✏</span>
    </div>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function NodeOverviewTab({ node, onRenamed, onRefresh }) {
  const isMobile = useIsMobile();
  const [running, setRunning] = React.useState({});
  const [pending, setPending] = React.useState(null); // { scan, label, queuedAt, confirmed }
  const [errorMsg, setErrorMsg] = React.useState('');
  const [, setTick] = React.useState(0);

  // Watch for the next heartbeat after an action is queued — confirms it was picked up
  React.useEffect(() => {
    if (!pending || pending.confirmed) return;
    const hbTs = node.last_heartbeat || node.last_seen;
    if (!hbTs) return;
    if (new Date(hbTs).getTime() > pending.queuedAt) {
      setPending(p => p ? { ...p, confirmed: true } : null);
      setTimeout(() => setPending(null), 4000);
    }
  }, [node.last_heartbeat, node.last_seen, pending]);

  const queueAction = (scan) => {
    setPending({ scan, label: SCAN_LABELS[scan] || scan, queuedAt: Date.now(), confirmed: false });
  };

  const forceRun = async (scan) => {
    setRunning(r => ({...r, [scan]: true}));
    setErrorMsg('');
    try {
      const r = await authFetch(`/api/edge/nodes/${node.edge_id}/run`, {
        method: 'POST',
        body: JSON.stringify({ scan }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      queueAction(scan);
    } catch (e) {
      setErrorMsg(`Error: ${e.message}`);
      setTimeout(() => setErrorMsg(''), 4000);
    } finally {
      setRunning(r => ({...r, [scan]: false}));
    }
  };

  const restartServices = async () => {
    if (!window.confirm(`Restart all containers on ${node.label || node.edge_id.substring(0, 8)}? Services will be offline for ~30 seconds.`)) return;
    setRunning(r => ({...r, restart: true}));
    setErrorMsg('');
    try {
      const r = await authFetch(`/api/edge/nodes/${node.edge_id}/run`, {
        method: 'POST',
        body: JSON.stringify({ scan: 'restart_services' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      queueAction('restart_services');
    } catch (e) {
      setErrorMsg(`Error: ${e.message}`);
      setTimeout(() => setErrorMsg(''), 4000);
    } finally {
      setRunning(r => ({...r, restart: false}));
    }
  };

  const rebootNode = async () => {
    if (!window.confirm(`Reboot the NAS at ${node.label || node.edge_id.substring(0, 8)}?\n\nThe NAS and all edge services will be offline for several minutes.`)) return;
    setRunning(r => ({...r, reboot: true}));
    setErrorMsg('');
    try {
      const r = await authFetch(`/api/edge/nodes/${node.edge_id}/run`, {
        method: 'POST',
        body: JSON.stringify({ scan: 'reboot_node' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      queueAction('reboot_node');
    } catch (e) {
      setErrorMsg(`Error: ${e.message}`);
      setTimeout(() => setErrorMsg(''), 4000);
    } finally {
      setRunning(r => ({...r, reboot: false}));
    }
  };

  const updateImages = async () => {
    if (!window.confirm(`Pull latest container images and recreate all services on ${node.label || node.edge_id.substring(0, 8)}?\n\nServices will be offline for ~1-2 minutes while images download and containers restart.`)) return;
    setRunning(r => ({...r, update: true}));
    setErrorMsg('');
    try {
      const r = await authFetch(`/api/edge/nodes/${node.edge_id}/run`, {
        method: 'POST',
        body: JSON.stringify({ scan: 'update_images' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      queueAction('update_images');
    } catch (e) {
      setErrorMsg(`Error: ${e.message}`);
      setTimeout(() => setErrorMsg(''), 4000);
    } finally {
      setRunning(r => ({...r, update: false}));
    }
  };

  React.useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 15000);
    return () => clearInterval(t);
  }, []);

  const cfg            = node.config || {};
  const hbIntervalSecs = (cfg.heartbeat_interval_mins || 15) * 60;
  const csStatus       = heartbeatStatus(node);
  const csColor        = CONNECTIVITY_COLORS[csStatus];
  const hbTs           = node.last_heartbeat || node.last_seen;
  const hbCountdown    = countdownTo(hbTs, hbIntervalSecs);
  const hbCountColor   = nextRunColor(hbTs, hbIntervalSecs);
  const scanRows       = getScanRows(node);
  const components     = node.health?.components || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Connectivity strip: v2.48 status and the node's numbers on one row, then the
          buttons across the full width, with the desktop login and notes below them */}
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border)',
        borderTop: `3px solid ${csColor}`, borderRadius: 10,
        padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <StatusDot color={csColor} pulse={csStatus === 'online'} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: csColor, lineHeight: 1 }}>
                {CONNECTIVITY_LABELS[csStatus]}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                {hbTs ? `Last contact ${timeAgo(hbTs)}` : 'No contact yet'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Next Ping</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: hbCountColor, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>{hbCountdown === 'overdue' ? 'Overdue' : (hbCountdown || '—')}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>every {cfg.heartbeat_interval_mins || 15} min</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Edge ID</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-mono)', lineHeight: 1 }}>{node.edge_id.substring(0, 8)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                since {node.created_at ? new Date(node.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Europe/London' }) : '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>NAS IP</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-mono)', lineHeight: 1 }}>{node.nas_ip || node.lan_ip || '—'}</div>
                {(node.nas_ip || node.lan_ip) && (
                  <button className="btn btn-quiet btn-sm btn-icon" title="Copy the NAS IP"
                    onClick={() => { try { navigator.clipboard.writeText(node.nas_ip || node.lan_ip); } catch (e) { /* clipboard blocked */ } }}>
                    <i className="ti ti-copy" aria-hidden="true" />
                  </button>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {node.nas_ip && node.lan_ip && node.nas_ip !== node.lan_ip
                  ? `edge reports ${node.lan_ip}`
                  : (node.nas_ip || node.lan_ip) ? 'private, on the client LAN' : 'not reported yet'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>QuickConnect</div>
              <QuickConnectEditor node={node} onUpdated={onRenamed} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {node.quickconnect_id ? 'click to edit' : 'click to add'}
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
          <a href={`https://cias-${node.edge_id.substring(0, 8)}.stg.limited`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
            Compliance Portal ↗
          </a>
          <a href={`https://cowe-${node.edge_id.substring(0, 8)}.stg.limited`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
            Web Terminal ↗
          </a>
          {node.quickconnect_id && (
            <a href={`https://quickconnect.to/${node.quickconnect_id}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
              NAS Console ↗
            </a>
          )}
          {/* last, so its login line and notes run full width under every button */}
          <LinuxDesktopControls node={node} onRefresh={onRefresh} />
        </div>
      </div>

      {/* Scan schedule */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
          Scan Schedule
        </div>
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {scanRows.map(row => {
              const hasRun        = !!row.lastRun;
              const countdown     = hasRun ? countdownTo(row.lastRun, row.intervalSecs) : null;
              const countColor    = hasRun ? nextRunColor(row.lastRun, row.intervalSecs) : '#86868b';
              const isOverdue     = countdown === 'overdue';
              const intervalLabel = row.intervalSecs >= 3600 ? `${row.intervalSecs / 3600}h` : `${row.intervalSecs / 60}m`;
              return (
                <div key={row.key} style={{
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderLeft: `3px solid ${countColor}`, borderRadius: 10, padding: '12px 14px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{row.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{row.description}</div>
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                      background: `${countColor}18`, border: `1px solid ${countColor}40`,
                      color: countColor, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', flexShrink: 0,
                    }}>
                      {!hasRun ? 'Awaiting' : isOverdue ? 'Overdue' : 'On Schedule'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 2 }}>Last Run</div>
                        <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: hasRun ? 'var(--text)' : 'var(--text-muted)' }}>{hasRun ? timeAgo(row.lastRun) : '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 2 }}>Next Run</div>
                        <div style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-mono)', color: countColor }}>{countdown ? (isOverdue ? 'Overdue' : `in ${countdown}`) : '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 2 }}>Interval</div>
                        <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{intervalLabel}</div>
                      </div>
                    </div>
                    <button className="btn btn-secondary btn-sm" style={{ opacity: (running[row.key] || !!pending) ? 0.5 : 1, flexShrink: 0 }} onClick={() => forceRun(row.key)} disabled={running[row.key] || !!pending}
                     >
                      {running[row.key] ? '…' : '↺ Run'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
          overflowX: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-x',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--card-bg)' }}>
                {['Scan', 'Last Run', 'Next Run', 'Interval', 'Status', ''].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 600,
                    letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)',
                    fontFamily: 'var(--font-mono)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scanRows.map((row, i) => {
                const hasRun        = !!row.lastRun;
                const countdown     = hasRun ? countdownTo(row.lastRun, row.intervalSecs) : null;
                const countColor    = hasRun ? nextRunColor(row.lastRun, row.intervalSecs) : '#86868b';
                const isOverdue     = countdown === 'overdue';
                const intervalLabel = row.intervalSecs >= 3600 ? `${row.intervalSecs / 3600}h` : `${row.intervalSecs / 60}m`;
                return (
                  <tr key={row.key} style={{ borderBottom: i < scanRows.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{row.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{row.description}</div>
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontSize: 12, color: hasRun ? 'var(--text)' : 'var(--text-muted)' }}>
                      {hasRun ? timeAgo(row.lastRun) : '—'}
                      {hasRun && (
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                          {fmtDt(row.lastRun)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: countColor, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
                        {countdown ? (isOverdue ? 'Overdue' : `in ${countdown}`) : '—'}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      {intervalLabel}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                        background: `${countColor}18`, border: `1px solid ${countColor}40`,
                        color: countColor, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
                      }}>
                        {!hasRun ? 'Awaiting' : isOverdue ? 'Overdue' : 'On Schedule'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button className="btn btn-secondary btn-sm" style={{ opacity: (running[row.key] || !!pending) ? 0.5 : 1 }} onClick={() => forceRun(row.key)} disabled={running[row.key] || !!pending}
                       >
                        {running[row.key] ? '…' : '↺ Run'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, minHeight: 18 }}>
          {errorMsg && (
            <span style={{ color: '#ff2d55' }}>{errorMsg}</span>
          )}
          {!errorMsg && pending && !pending.confirmed && (
            <span style={{ color: 'var(--accent)' }}>
              ⏳ {pending.label} queued — waiting for next heartbeat
              {hbCountdown && hbCountdown !== 'overdue' ? ` (in ${hbCountdown})` : ''}
            </span>
          )}
          {!errorMsg && pending?.confirmed && (
            <span style={{ color: '#30d158' }}>✓ {pending.label} picked up by heartbeat</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-danger btn-sm" style={{ opacity: (Object.values(running).some(Boolean) || !!pending) ? 0.5 : 1 }} onClick={rebootNode} disabled={Object.values(running).some(Boolean) || !!pending}
           >
            {running.reboot ? '…' : '⏻ Reboot NAS'}
          </button>
          <button className="btn btn-danger btn-sm" style={{ opacity: (Object.values(running).some(Boolean) || !!pending) ? 0.5 : 1 }} onClick={restartServices} disabled={Object.values(running).some(Boolean) || !!pending}
           >
            {running.restart ? '…' : '↺ Restart Services'}
          </button>
          <button className="btn btn-secondary btn-sm" style={{ color: '#e6b800', opacity: (Object.values(running).some(Boolean) || !!pending) ? 0.5 : 1 }} onClick={updateImages} disabled={Object.values(running).some(Boolean) || !!pending}
           >
            {running.update ? '…' : '↑ Pull newer images'}
          </button>
          <button className="btn btn-secondary btn-sm" style={{ opacity: (Object.values(running).some(Boolean) || !!pending) ? 0.5 : 1 }} onClick={() => forceRun('all')} disabled={Object.values(running).some(Boolean) || !!pending}
           >
            ↺ Run All Now
          </button>
        </div>
      </div>

      {/* Service health */}
      {components.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
            Service Health
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {components.map((c, i) => <HealthCard key={i} component={c} />)}
          </div>
          {node.health?.pushed_at && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
              Updated {fmtDt(node.health.pushed_at)}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

// ── Compliance tab ────────────────────────────────────────────────────────────

function NodeComplianceTab({ node }) {
  const ciasUrl = `https://cias-${node.edge_id.substring(0, 8)}.stg.limited`;
  const [compliance, setCompliance] = React.useState(null);
  const [loading, setLoading]       = React.useState(true);

  React.useEffect(() => {
    authFetch(`/api/clients/${node.client_slug}/compliance`)
      .then(r => r.json())
      .then(d => { setCompliance(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [node.client_slug]);

  const FW_IDENTITY = { 'Cyber Essentials': '#0a84ff', 'General Data Protection Regulation': '#bf5af2', 'ISO/IEC 27001': '#5856d6' };
  const ragColor = (p) => p === 0 ? '#86868b' : p < 50 ? '#ff2d55' : p < 80 ? '#e6b800' : '#30d158';
  const FW_LABELS = { 'Cyber Essentials': 'Cyber Essentials', 'General Data Protection Regulation': 'GDPR', 'ISO/IEC 27001': 'ISO 27001' };

  const posture    = compliance?.data?.posture || {};
  const frameworks = compliance?.data?.frameworks || Object.keys(posture);
  const pushedAt   = compliance?.pushed_at;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border)',
        borderTop: '3px solid var(--accent)', borderRadius: 10,
        padding: '20px 24px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0, flex: '1 1 240px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Compliance Portal</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Full compliance management for{' '}
            <strong style={{ color: 'var(--text)' }}>{node.label || node.client_slug}</strong>.
          </div>
          <code style={{ fontSize: 12, color: 'var(--accent)', marginTop: 6, display: 'block', overflowWrap: 'break-word', wordBreak: 'break-all' }}>{ciasUrl}</code>
        </div>
        <a href={ciasUrl} target="_blank" rel="noreferrer" className="btn btn-primary btn-lg" style={{ flexShrink: 0 }}>
          Go to Portal
        </a>
      </div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center' }}><span className="spinner" /></div>
      ) : !compliance?.available ? (
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '24px', textAlign: 'center',
          color: 'var(--text-muted)', fontSize: 13,
        }}>
          No compliance data pushed yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              Compliance Posture
            </div>
            {pushedAt && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Updated {fmtDt(pushedAt)}
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {frameworks.map((fw, i) => {
              const data  = posture[fw] || {};
              const identity = FW_IDENTITY[fw] || '#86868b';
              const label    = FW_LABELS[fw] || fw;
              const reqs  = data.requirements || {};
              const total = reqs.total_assessable || 0;
              const pct   = reqs.percent_compliant || 0;
              const ragCol   = ragColor(pct);
              if (data.not_configured) return (
                <div key={fw} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px', opacity: 0.5 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>{label}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Not configured</div>
                </div>
              );
              return (
                <div key={fw} style={{
                  background: 'var(--card-bg)', border: '1px solid var(--border)',
                  borderTop: `3px solid ${identity}`, borderRadius: 10, padding: '16px 18px',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>{label}</div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontSize: 32, fontWeight: 800, color: ragCol, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>{pct}%</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>{reqs.compliant || 0} / {total}<br />compliant</div>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: ragCol, borderRadius: 2, transition: 'width 0.6s ease' }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>{total} assessable requirements</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ── Node Health tab ───────────────────────────────────────────────────────────

const RAG_COLOR = { red: '#ff2d55', amber: '#e6b800', green: '#30d158', info: '#86868b' };
const RAG_LABEL = { red: 'Critical', amber: 'Warning', green: 'Healthy', info: 'Info' };

const IMAGE_STATUS_COLOR = { current: '#30d158', update_available: '#e6b800', pinned: '#86868b', locally_built: '#86868b', unknown: '#86868b' };
const IMAGE_STATUS_LABEL = { current: 'Current', update_available: 'Newer image', pinned: 'Pinned', locally_built: 'Locally built', unknown: 'Unknown' };

function shortDigest(d) {
  if (!d) return null;
  const hex = d.startsWith('sha256:') ? d.slice(7) : d;
  return hex.slice(0, 12);
}

function ImageUpdateRow({ img }) {
  const color = IMAGE_STATUS_COLOR[img.status] || '#86868b';
  const label = IMAGE_STATUS_LABEL[img.status] || img.status;
  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '10px 14px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <StatusDot color={color} />
        <span style={{
          fontSize: 12, color: 'var(--text)', fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere', minWidth: 0,
        }}>{img.image}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {img.status === 'pinned' && img.tag_newer && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}
            title="compose.yml pins this image to one build; change the pin there to move to the newer build">
            Newer build of this tag published
          </span>
        )}
        {img.status === 'update_available' && shortDigest(img.remote_digest) && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            → {shortDigest(img.remote_digest)}
          </span>
        )}
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
          background: `${color}20`, border: `1px solid ${color}60`, color, whiteSpace: 'nowrap',
        }}>{label}</span>
      </div>
    </div>
  );
}

function FindingRow({ finding }) {
  const color = RAG_COLOR[finding.severity] || '#86868b';
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '10px 0', borderBottom: '1px solid var(--border)',
    }}>
      <div style={{
        width: 6, height: 6, borderRadius: '50%', background: color,
        flexShrink: 0, marginTop: 5,
      }} />
      <div style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>{finding.text}</div>
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
        background: `${color}20`, border: `1px solid ${color}60`, color,
        whiteSpace: 'nowrap', flexShrink: 0,
      }}>{RAG_LABEL[finding.severity] || finding.severity}</span>
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: 'var(--text-muted)',
      marginTop: 20, marginBottom: 8,
    }}>{title}</div>
  );
}

function InfoGrid({ items }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
      gap: 10,
    }}>
      {items.filter(Boolean).map(({ label, value, mono, color }) => (
        <div key={label} style={{
          background: 'var(--card-bg)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '10px 14px',
        }}>
          <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: color || 'var(--text)', fontFamily: mono ? 'var(--font-mono)' : 'inherit' }}>{value ?? '—'}</div>
        </div>
      ))}
    </div>
  );
}

function ServiceRow({ name, svc }) {
  if (!svc) return null;
  const enabled = svc.enabled;
  const color   = enabled ? (svc.risk === 'critical' ? '#ff2d55' : '#e6b800') : '#30d158';
  const label   = enabled ? (svc.risk === 'critical' ? 'Enabled — Critical' : 'Enabled') : 'Disabled';
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '10px 0', borderBottom: '1px solid var(--border)',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
          {name}{svc.port ? <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginLeft: 6 }}>:{svc.port}</span> : null}
          {name === 'FTP' && svc.sftp_enabled && <span style={{ fontSize: 11, color: '#30d158', marginLeft: 8 }}>SFTP available</span>}
          {name === 'SNMP' && svc.version && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>v{svc.version}</span>}
        </div>
        {svc.note && enabled && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{svc.note}</div>}
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
        background: `${color}20`, border: `1px solid ${color}60`, color,
        whiteSpace: 'nowrap', flexShrink: 0,
      }}>{label}</span>
    </div>
  );
}

function NodeHealthTab({ node }) {
  const isMobile = useIsMobile();
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState('');
  const [running, setRunning] = React.useState(false);
  const [runMsg,  setRunMsg]  = React.useState('');

  const load = React.useCallback(() => {
    setLoading(true);
    authFetch(`/api/edge/nodes/${node.edge_id}/node-health`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [node.edge_id]);

  React.useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await authFetch(`/api/edge/nodes/${node.edge_id}/run`, {
        method: 'POST', body: JSON.stringify({ scan: 'node_health' }),
      });
      const d = await r.json();
      setRunMsg(r.ok ? 'Node health check queued — results will appear after next run' : d.error || 'Failed');
      setTimeout(() => setRunMsg(''), 6000);
    } catch (e) {
      setRunMsg(e.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><span className="spinner" /></div>;
  if (error)   return <div className="error-msg">{error}</div>;

  if (!data?.available) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Container Services */}
      {(() => {
        const serviceComp = (node.health?.components || []).find(c => c.label === 'Services');
        if (!serviceComp) return null;
        const allOk  = serviceComp.status === 'ok';
        const color  = HEALTH_COLORS[serviceComp.status] || '#86868b';
        const KNOWN  = [
          ['stgedge-heartbeat',        'Heartbeat'],
          ['stgedge-backup-check',     'Backup Check'],
          ['stgedge-node-check',       'Node Check'],
          ['stgedge-scan-scheduler',   'Scan Scheduler'],
          ['stgedge-ciso-push',        'Compliance Sync'],
          ['stgedge-ciso-huey',        'Compliance Queue'],
          ['stgedge-ciso-backend',     'Compliance Backend'],
          ['stgedge-ciso-frontend',    'Compliance Frontend'],
          ['stgedge-ciso-caddy',       'Compliance Proxy'],
          ['stgedge-redis',            'Redis Cache'],
          ['stgedge-cloudflared-cias', 'Compliance Tunnel'],
          ['stgedge-iperf3',           'Network Speed Test'],
          ['stgedge-cowe',             'Web Console'],
          ['stgedge-cloudflared-cowe', 'Web Console Tunnel'],
        ];
        const msgLower = (serviceComp.message || '').toLowerCase();
        const problems = allOk ? [] : KNOWN.filter(([, n]) => msgLower.includes(n.toLowerCase())).map(([, n]) => n);
        return (
          <>
            <SectionHeader title="Container Services" />
            <div style={{
              background: 'var(--card-bg)', border: '1px solid var(--border)',
              borderLeft: `3px solid ${color}`, borderRadius: 10, padding: '14px 16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{serviceComp.message}</div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                  background: `${color}20`, border: `1px solid ${color}60`, color,
                  whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)',
                }}>{allOk ? 'OK' : 'Issue'}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
                {KNOWN.map(([, name]) => {
                  const hasProblem = problems.includes(name);
                  const dotColor   = allOk ? '#30d158' : hasProblem ? '#ff2d55' : '#30d158';
                  return (
                    <div key={name} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: 'var(--surface)', border: `1px solid ${hasProblem ? '#ff2d5540' : 'var(--border)'}`,
                      borderRadius: 6, padding: '7px 10px',
                    }}>
                      <StatusDot color={dotColor} pulse={dotColor === '#30d158'} />
                      <span style={{ fontSize: 12, color: hasProblem ? '#ff2d55' : 'var(--text)', fontWeight: hasProblem ? 700 : 400 }}>{name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        );
      })()}
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border)',
        borderRadius: 10, padding: '32px', textAlign: 'center',
        color: 'var(--text-muted)', fontSize: 13,
      }}>
        No node health data yet. The check runs daily at 07:30 GMT, or you can trigger it manually.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn btn-primary btn-sm"
          disabled={running} onClick={runNow}>
          {running ? <span className="spinner" /> : '↺ Run Now'}
        </button>
        {runMsg && <div style={{ fontSize: 12, color: 'var(--accent)' }}>{runMsg}</div>}
      </div>
    </div>
  );

  const nh       = data.data;
  const overall  = nh.overall || 'green';
  const findings = nh.findings || [];
  const dsm      = nh.dsm || {};
  const volumes  = nh.volumes || [];
  const users    = nh.users || [];
  const firewall = nh.firewall || {};
  const services = nh.services || {};
  const https    = nh.https || {};
  const sa       = nh.security_advisor || [];
  const autoblock= nh.autoblock || {};
  const imageUpdates = nh.image_updates || [];
  const overallColor = RAG_COLOR[overall] || '#86868b';

  const saFail = sa.filter(c => c.result === 'fail').length;
  const saWarn = sa.filter(c => c.result === 'warning').length;
  const saPass = sa.filter(c => c.result === 'pass').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

      {/* Overall banner */}
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border)',
        borderTop: `3px solid ${overallColor}`, borderRadius: 10,
        padding: '16px 20px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <StatusDot color={overallColor} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: overallColor }}>{RAG_LABEL[overall] || overall}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {findings.length === 0 ? 'No issues found' : `${findings.length} issue${findings.length !== 1 ? 's' : ''} found`}
              {nh.scanned_at ? ` · Updated ${fmtDt(nh.scanned_at)}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm"
            disabled={running} onClick={runNow}>
            {running ? <span className="spinner" /> : '↺ Run Now'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            ↻ Refresh
          </button>
        </div>
      </div>
      {runMsg && <div style={{ fontSize: 12, color: 'var(--accent)', padding: '4px 0' }}>{runMsg}</div>}
      {/* Container Services */}
      {(() => {
        const serviceComp = (node.health?.components || []).find(c => c.label === 'Services');
        if (!serviceComp) return null;
        const allOk  = serviceComp.status === 'ok';
        const color  = HEALTH_COLORS[serviceComp.status] || '#86868b';
        const KNOWN  = [
          ['stgedge-heartbeat',        'Heartbeat'],
          ['stgedge-backup-check',     'Backup Check'],
          ['stgedge-node-check',       'Node Check'],
          ['stgedge-scan-scheduler',   'Scan Scheduler'],
          ['stgedge-ciso-push',        'Compliance Sync'],
          ['stgedge-ciso-huey',        'Compliance Queue'],
          ['stgedge-ciso-backend',     'Compliance Backend'],
          ['stgedge-ciso-frontend',    'Compliance Frontend'],
          ['stgedge-ciso-caddy',       'Compliance Proxy'],
          ['stgedge-redis',            'Redis Cache'],
          ['stgedge-cloudflared-cias', 'Compliance Tunnel'],
          ['stgedge-iperf3',           'Network Speed Test'],
          ['stgedge-cowe',             'Web Console'],
          ['stgedge-cloudflared-cowe', 'Web Console Tunnel'],
        ];
        const msgLower = (serviceComp.message || '').toLowerCase();
        const problems = allOk ? [] : KNOWN.filter(([, n]) => msgLower.includes(n.toLowerCase())).map(([, n]) => n);
        return (
          <>
            <SectionHeader title="Container Services" />
            <div style={{
              background: 'var(--card-bg)', border: '1px solid var(--border)',
              borderLeft: `3px solid ${color}`, borderRadius: 10, padding: '14px 16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{serviceComp.message}</div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                  background: `${color}20`, border: `1px solid ${color}60`, color,
                  whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)',
                }}>{allOk ? 'OK' : 'Issue'}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
                {KNOWN.map(([, name]) => {
                  const hasProblem = problems.includes(name);
                  const dotColor   = allOk ? '#30d158' : hasProblem ? '#ff2d55' : '#30d158';
                  return (
                    <div key={name} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: 'var(--surface)', border: `1px solid ${hasProblem ? '#ff2d5540' : 'var(--border)'}`,
                      borderRadius: 6, padding: '7px 10px',
                    }}>
                      <StatusDot color={dotColor} pulse={dotColor === '#30d158'} />
                      <span style={{ fontSize: 12, color: hasProblem ? '#ff2d55' : 'var(--text)', fontWeight: hasProblem ? 700 : 400 }}>{name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        );
      })()}
      {/* Container Images */}
      {imageUpdates.length > 0 && (() => {
        const updateCount = imageUpdates.filter(i => i.status === 'update_available').length;
        const pinnedCount = imageUpdates.filter(i => i.status === 'pinned').length;
        return (
          <>
            <SectionHeader title="Container Images" />
            <div style={{
              background: 'var(--card-bg)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '10px 16px', marginBottom: 8,
              fontSize: 12, color: 'var(--text-muted)',
            }}>
              {updateCount === 0
                ? 'All images current with their upstream registry.'
                : `${updateCount} newer image${updateCount !== 1 ? 's' : ''} published, reviewed manually, not auto-applied.`}
              {pinnedCount > 0 && ` ${pinnedCount} pinned to a build in compose.yml, changed only by editing the pin.`}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {imageUpdates.map((img, i) => <ImageUpdateRow key={i} img={img} />)}
            </div>
          </>
        );
      })()}
{/* Findings */}
      {findings.length > 0 && (
        <>
          <SectionHeader title="Findings" />
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 16px' }}>
            {findings.map((f, i) => <FindingRow key={i} finding={f} />)}
          </div>
        </>
      )}

      {/* DSM */}
      <SectionHeader title="NAS System" />
      <InfoGrid items={[
        { label: 'DSM Version',  value: dsm.version },
        { label: 'Model',        value: dsm.model },
        { label: 'DSM', value: dsm.update_available ? `Update available${dsm.update_version ? ` (${dsm.update_version})` : ''}`
            : dsm.update_check === 'failed' ? 'Not checked' : 'Up to date',
          color: dsm.update_available ? '#e6b800' : dsm.update_check === 'failed' ? '#86868b' : '#30d158' },
        ...(dsm.update_checked_at ? [{ label: 'DSM checked',
          value: new Date(dsm.update_checked_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT'
            + (dsm.update_check === 'nas' ? ' by the NAS' : dsm.update_check === 'feed' ? " from Synology's update list" : '') }] : []),
        { label: 'Uptime',       value: dsm.uptime },
      ]} />

      {/* Volumes */}
      {volumes.length > 0 && (
        <>
          <SectionHeader title="Storage Volumes" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {volumes.map((v, i) => (
              <div key={i} style={{
                background: 'var(--card-bg)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '12px 16px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{v.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v.used} / {v.total}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: RAG_COLOR[v.rag] || '#86868b',
                      background: `${RAG_COLOR[v.rag] || '#86868b'}20`, border: `1px solid ${RAG_COLOR[v.rag] || '#86868b'}60`,
                      padding: '2px 7px', borderRadius: 4,
                    }}>{v.pct}%</span>
                  </div>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${v.pct}%`, background: RAG_COLOR[v.rag] || '#86868b', borderRadius: 2, transition: 'width 0.6s ease' }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{v.free} free · Status: {v.status}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Users */}
      {users.length > 0 && (
        <>
          <SectionHeader title="User Accounts" />
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {users.map((u, i) => {
                const mfaColor = u.mfa ? '#30d158' : (u.is_admin ? '#ff2d55' : '#e6b800');
                return (
                  <div key={i} style={{
                    background: 'var(--card-bg)', border: '1px solid var(--border)',
                    borderRadius: 8, padding: '10px 14px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: u.is_admin ? '#e6b800' : 'var(--text-muted)', marginTop: 2 }}>
                        {u.is_admin ? 'Admin' : 'User'} · {u.disabled ? 'Disabled' : 'Active'}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: mfaColor, background: `${mfaColor}20`, border: `1px solid ${mfaColor}40`, padding: '2px 7px', borderRadius: 4, flexShrink: 0 }}>
                      2FA {u.mfa ? 'On' : 'Off'}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10,
            overflowX: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-x',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
                  {['Username', 'Role', '2FA', 'Status'].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => {
                  const mfaColor = u.mfa ? '#30d158' : (u.is_admin ? '#ff2d55' : '#e6b800');
                  return (
                    <tr key={i} style={{ borderBottom: i < users.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{u.name}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: u.is_admin ? '#e6b800' : 'var(--text-muted)' }}>{u.is_admin ? 'Admin' : 'User'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: mfaColor, background: `${mfaColor}20`, border: `1px solid ${mfaColor}40`, padding: '2px 7px', borderRadius: 4 }}>
                          {u.mfa ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: u.disabled ? 'var(--text-muted)' : '#30d158' }}>{u.disabled ? 'Disabled' : 'Active'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}

      {/* Security */}
      <SectionHeader title="Security Configuration" />
      <InfoGrid items={[
        { label: 'Firewall',     value: firewall.enabled === null ? 'Unknown' : firewall.enabled ? 'Enabled' : 'Disabled', color: firewall.enabled ? '#30d158' : '#ff2d55' },
        { label: 'Auto-block',   value: autoblock.enabled === null ? 'Unknown' : autoblock.enabled ? 'Enabled' : 'Disabled', color: autoblock.enabled ? '#30d158' : '#e6b800' },
        { label: 'HTTPS Redirect', value: https.https_redirect === null ? 'Unknown' : https.https_redirect ? 'Enabled' : 'Disabled', color: https.https_redirect ? '#30d158' : '#e6b800' },
        { label: 'HSTS',         value: https.hsts === null ? 'Unknown' : https.hsts ? 'Enabled' : 'Disabled', color: https.hsts ? '#30d158' : '#e6b800' },
      ]} />

      {/* Services */}
      {Object.keys(services).length > 0 && (
        <>
          <SectionHeader title="Network Services" />
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 16px' }}>
            {Object.entries(services).map(([name, svc]) => <ServiceRow key={name} name={name} svc={svc} />)}
          </div>
        </>
      )}

      {/* Security Advisor */}
      {sa.length > 0 && (
        <>
          <SectionHeader title="Security Advisor" />
          <div className="stat-tiles">
            {[
              { label: 'Pass',    value: saPass, tone: 'green' },
              { label: 'Warning', value: saWarn, tone: saWarn ? 'amber' : 'muted' },
              { label: 'Fail',    value: saFail, tone: saFail ? 'red' : 'muted' },
            ].map(({ label, value, tone }) => (
              <div key={label} className="stat-card">
                <div className={`stat-value ${tone}`}>{value}</div>
                <div className="stat-label">{label}</div>
              </div>
            ))}
          </div>
        </>
      )}

    </div>
  );
}


// ── Schedule tab ─────────────────────────────────────────────────────────────

function ScheduleTab({ node }) {
  const [config,  setConfig]  = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving,  setSaving]  = React.useState(false);
  const [msg,     setMsg]     = React.useState(null);

  const defaults = {
    heartbeat_interval_mins:  5,
    scan_interval_hours:      4,
    backup_schedule:          "07:00",
    node_check_schedule:      "07:30",
    ciso_push_interval_hours: 4,
    weto_auto_stop_mins:      240,
  };

  React.useEffect(() => {
    authFetch(`/api/edge/nodes/${node.edge_id}/config`)
      .then(r => r.json())
      .then(d => { setConfig({...defaults, ...d}); setLoading(false); })
      .catch(() => { setConfig(defaults); setLoading(false); });
  }, [node.edge_id]);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 5000);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/edge/nodes/${node.edge_id}/config`, {
        method: 'PUT', body: JSON.stringify(config),
      });
      const d = await res.json();
      flash(d.message || d.error, res.ok);
    } catch (e) { flash(e.message, false); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><span className="spinner" /></div>;

  const Row = ({ label, sublabel, children }) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 16, padding: '14px 0', borderBottom: '1px solid var(--border)',
      flexWrap: 'wrap',
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
        {sublabel && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sublabel}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );

  const TimeInput = ({ value, onChange }) => (
    <input className="input-sm" type="time" value={value || ''}
      onChange={e => onChange(e.target.value)}/>
  );

  const SelectInput = ({ value, onChange, options }) => (
    <select className="input-sm" value={value} onChange={e => onChange(Number(e.target.value))}
      style={{ cursor: 'pointer' }}>
      {options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
    </select>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 20px' }}>

        <Row label="Heartbeat Interval"
          sublabel="How often the Edge node checks in with Clearview. Changes take effect immediately.">
          <SelectInput value={config.heartbeat_interval_mins}
            onChange={v => set('heartbeat_interval_mins', v)}
            options={[[1,'Every 1 minute'],[2,'Every 2 minutes'],[5,'Every 5 minutes'],[10,'Every 10 minutes'],[15,'Every 15 minutes'],[30,'Every 30 minutes']]} />
        </Row>

        <Row label="Network Scan Interval"
          sublabel="How often the network is scanned for devices and open ports.">
          <SelectInput value={config.scan_interval_hours}
            onChange={v => set('scan_interval_hours', v)}
            options={[[1,'Every 1 hour'],[2,'Every 2 hours'],[4,'Every 4 hours'],[8,'Every 8 hours'],[12,'Every 12 hours'],[24,'Every 24 hours']]} />
        </Row>

        <Row label="Compliance Sync Interval"
          sublabel="How often compliance framework progress is pushed to Clearview.">
          <SelectInput value={config.ciso_push_interval_hours}
            onChange={v => set('ciso_push_interval_hours', v)}
            options={[[1,'Every 1 hour'],[2,'Every 2 hours'],[4,'Every 4 hours'],[6,'Every 6 hours'],[12,'Every 12 hours'],[24,'Every 24 hours']]} />
        </Row>

        <Row label="Backup Check Time (GMT)"
          sublabel="Daily time to check backup status and push results to Clearview.">
          <TimeInput value={config.backup_schedule} onChange={v => set('backup_schedule', v)} />
        </Row>

        <Row label="Node Health Check Time (GMT)"
          sublabel="Daily time to run the NAS security and health baseline check.">
          <TimeInput value={config.node_check_schedule} onChange={v => set('node_check_schedule', v)} />
        </Row>

        <Row label="Linux Desktop Auto-Stop"
          sublabel="How long the on-demand Linux desktop runs before it is stopped automatically. Applies to a desktop already running, timed from when it started.">
          <SelectInput value={config.weto_auto_stop_mins}
            onChange={v => set('weto_auto_stop_mins', v)}
            options={[[30,'After 30 minutes'],[60,'After 1 hour'],[120,'After 2 hours'],[240,'After 4 hours'],[480,'After 8 hours'],[0,'Never stop']]} />
        </Row>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 0', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Linux Desktop Version</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              Set for every edge node in the edge compose.yml, with every other image version. A new version downloads overnight, or use Download Now on the Overview tab; a running desktop keeps its version until it is next started.
            </div>
          </div>
          <div style={{ flexShrink: 0, fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{config.weto_image_tag || WETO_DEFAULT_TAG}</div>
        </div>

      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-primary"
          disabled={saving} onClick={save}>
          {saving ? <span className="spinner" /> : 'Save Schedule'}
        </button>
        {msg && <div className={msg.ok ? 'success-msg' : 'error-msg'} style={{ margin: 0 }}>{msg.text}</div>}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Schedule changes are sent to the Edge node on the next heartbeat cycle and take effect immediately — no container restarts required.
      </div>
    </div>
  );
}

// ── Scripts tab ───────────────────────────────────────────────────────────────

function ScriptsTab({ node }) {
  const [manifest, setManifest] = React.useState(null);
  const [loading,  setLoading]  = React.useState(true);
  const [running,  setRunning]  = React.useState(false);
  const [msg,      setMsg]      = React.useState(null);

  React.useEffect(() => {
    authFetch('/api/edge/scripts/manifest')
      .then(r => r.json())
      .then(d => { setManifest(d.manifest || {}); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const localVersions = node.script_versions || {};

  const checkNow = async () => {
    setRunning(true);
    try {
      const r = await authFetch(`/api/edge/nodes/${node.edge_id}/run`, {
        method: 'POST',
        body: JSON.stringify({ scan: 'check_scripts' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      setMsg({ text: 'Script check queued — will apply on next heartbeat', ok: true });
      setTimeout(() => setMsg(null), 6000);
    } catch (e) {
      setMsg({ text: e.message, ok: false });
      setTimeout(() => setMsg(null), 5000);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><span className="spinner" /></div>;

  const files   = Object.keys(manifest || {});
  const behind  = files.filter(f => manifest[f] && localVersions[f] && manifest[f] !== localVersions[f]);
  const missing = files.filter(f => !localVersions[f]);
  const current = files.filter(f => manifest[f] && localVersions[f] === manifest[f]);

  const fileLabel = (path) => path.split('/').pop();
  const fileGroup = (path) => path.includes('/') ? path.split('/')[0] : 'root';

  const groups = {};
  files.forEach(f => {
    const g = fileGroup(f);
    if (!groups[g]) groups[g] = [];
    groups[g].push(f);
  });

  const statusFor = (f) => {
    if (!localVersions[f]) return { color: '#86868b', label: 'Not reported', icon: '?' };
    if (manifest[f] === localVersions[f]) return { color: '#30d158', label: 'Current', icon: '✓' };
    return { color: '#e6b800', label: 'Behind Core', icon: '↑' };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Summary tiles */}
      <div className="stat-tiles">
        {[
          { label: 'Up to date',       value: current.length, tone: 'green' },
          { label: 'Behind Core',      value: behind.length,  tone: behind.length ? 'amber' : 'muted' },
          { label: 'Not reported',     value: missing.length, tone: 'muted' },
        ].map(({ label, value, tone }) => (
          <div key={label} className="stat-card">
            <div className={`stat-value ${tone}`}>{value}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* File list grouped by directory */}
      {Object.entries(groups).map(([group, groupFiles]) => (
        <div key={group}>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8,
          }}>
            {group === 'root' ? 'Stack' : group}
          </div>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {groupFiles.map((f, i) => {
              const { color, label, icon } = statusFor(f);
              return (
                <div key={f} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 16px', gap: 12, flexWrap: 'wrap',
                  borderBottom: i < groupFiles.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text)', flex: 1, minWidth: 0 }}>
                    {fileLabel(f)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Node</div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: localVersions[f] ? 'var(--text)' : 'var(--text-muted)' }}>
                        {localVersions[f] || '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Core</div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text)' }}>
                        {manifest[f] || '—'}
                      </div>
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                      background: `${color}20`, border: `1px solid ${color}60`, color,
                      fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', minWidth: 100, textAlign: 'center',
                    }}>
                      {icon} {label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Scripts update automatically on each heartbeat. Node versions reflect the last reported check-in.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {msg && (
            <div style={{ fontSize: 12, color: msg.ok ? '#30d158' : '#ff2d55' }}>{msg.text}</div>
          )}
          <button className="btn btn-secondary btn-sm" style={{ opacity: running ? 0.5 : 1 }} onClick={checkNow} disabled={running}>
            {running ? '…' : '↺ Check Now'}
          </button>
        </div>
      </div>

      {!node.script_versions && (
        <div style={{
          fontSize: 12, color: 'var(--text-muted)', background: 'var(--card-bg)',
          border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px',
        }}>
          Node version data not yet available — updates on the next heartbeat from a node running edge-heartbeat.py v1.25 or later.
        </div>
      )}
    </div>
  );
}


// ── Main component ────────────────────────────────────────────────────────────

const REFRESH_OPTIONS = [
  [0,      'Off'],
  [10000,  '10s'],
  [30000,  '30s'],
  [60000,  '1m'],
  [120000, '2m'],
  [300000, '5m'],
];
const REFRESH_STORAGE_KEY = 'stg_edgenodes_refresh_ms';

// ── Edge images (v2.52) ───────────────────────────────────────────────────────
// Every edge image version lives in edge-dist/compose.yml. Images pinned to a build
// fall behind as their publishers rebuild the tag; Core checks daily (app.py v4.42).
// Move pin points a pin at the tag's current build; Roll out runs Update Images on the
// first node, then the rest. Per image: Manual, or Automatic after the set number of days.

const ROLLOUT_STAGE = {
  canary:  { label: 'First node',   tone: '#e6b800' },
  fleet:   { label: 'Other nodes',  tone: '#e6b800' },
  done:    { label: 'Finished',     tone: '#30d158' },
  failed:  { label: 'Stopped, first node failed', tone: '#ff2d55' },
  stopped: { label: 'Stopped by hand', tone: '#86868b' },
};
const ROLLOUT_NODE = {
  waiting: { label: 'Waiting',  tone: '#86868b' },
  queued:  { label: 'Updating', tone: '#e6b800' },
  done:    { label: 'Done',     tone: '#30d158' },
  failed:  { label: 'Failed',   tone: '#ff2d55' },
  removed: { label: 'Removed',  tone: '#86868b' },
};

function Tag({ text, tone }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap',
      background: `${tone}20`, border: `1px solid ${tone}60`, color: tone,
    }}>{text}</span>
  );
}

function shortSha(d) {
  if (!d) return '';
  return (d.startsWith('sha256:') ? d.slice(7) : d).slice(0, 12);
}

function ChangeVersionForm({ img, busy, rolloutActive, onDone, onCancel }) {
  const [tag, setTag]       = useState('');
  const [withSib, setWithSib] = useState(true);
  const [plan, setPlan]     = useState(null);
  const [working, setWorking] = useState(false);
  const [err, setErr]       = useState('');

  const [versions, setVersions] = useState(img.newer_versions || []);
  const [vLoading, setVLoading] = useState(true);
  const [vErr, setVErr] = useState('');

  useEffect(() => {
    let live = true;
    authFetch(`/api/edge/images/versions?key=${encodeURIComponent(img.key)}`).then(r => r.json()).then(d => {
      if (!live) return;
      if (d.error && !d.newer) setVErr(d.error);
      else { setVersions(d.newer || []); setVErr(d.error || ''); }
    }).catch(() => live && setVErr('Could not read the versions')).finally(() => live && setVLoading(false));
    return () => { live = false; };
  }, [img.key]);

  const call = async (body, useTag) => {
    setWorking(true); setErr('');
    try {
      const r = await authFetch('/api/edge/images/version', {
        method: 'POST', body: JSON.stringify({ key: img.key, tag: (useTag || tag).trim(), with_siblings: withSib, ...body }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      return d;
    } catch (e) {
      setErr(e.message); return null;
    } finally {
      setWorking(false);
    }
  };
  const check = async (useTag) => { setPlan(null); const d = await call({ check: true }, useTag); if (d) setPlan(d.plan); };
  const pick = (t) => { setTag(t); check(t); };
  const KIND_TONE = { patch: '#30d158', minor: '#0a84ff', major: '#e6b800' };
  const apply = async (rollout) => {
    const d = await call({ rollout });
    if (d) onDone(d, rollout ? `Version changed; roll-out started` : 'Version changed; roll out when ready');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 10px', borderRadius: 8,
      background: 'var(--surface-2, rgba(127,127,127,0.06))', fontSize: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)' }}>New tag for {img.name.split('/').pop()}</span>
        <input className="input-sm" style={{ width: 160 }} value={tag} autoFocus
          placeholder={img.tag || 'tag'} onChange={e => { setTag(e.target.value); setPlan(null); }}
          onKeyDown={e => { if (e.key === 'Enter' && tag.trim()) check(); }} />
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy || working || !tag.trim()} onClick={() => check()}>
          {working && !plan ? '…' : 'Check'}
        </button>
        <button type="button" className="btn btn-quiet btn-sm" onClick={onCancel}>Cancel</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)' }}>Newer versions:</span>
        {vLoading && versions.length === 0 && <span style={{ color: 'var(--text-muted)' }}>checking…</span>}
        {!vLoading && versions.length === 0 && (
          <span style={{ color: 'var(--text-muted)' }}>
            {vErr ? `could not read them (${vErr})` : img.tag ? `none newer than ${img.tag}` : 'pinned by build only, type a tag'}
          </span>
        )}
        {versions.slice(0, 10).map(v => (
          <button key={v.tag} type="button" className="btn btn-secondary btn-sm" disabled={busy || working}
            aria-pressed={tag === v.tag} onClick={() => pick(v.tag)} title={`${v.kind} update`}>
            {v.tag} <span style={{ color: KIND_TONE[v.kind] }}>{v.kind}</span>
          </button>
        ))}
      </div>
      {img.siblings && img.siblings.length > 0 && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0,
          textTransform: 'none', letterSpacing: 'normal', fontWeight: 400, fontSize: 12 }}>
          <input type="checkbox" checked={withSib} onChange={e => { setWithSib(e.target.checked); setPlan(null); }}
            style={{ width: 14, height: 14, accentColor: 'var(--accent)', margin: 0, flexShrink: 0 }} />
          <span style={{ color: 'var(--text-muted)' }}>
            Also change {img.siblings.map(k => k.split('/').pop()).join(', ')} (released together)
          </span>
        </label>
      )}
      {err && <div style={{ color: '#ff2d55' }}>{err}</div>}
      {plan && (
        <>
          {plan.map(st => (
            <div key={st.key} style={{ fontFamily: 'var(--font-mono)', fontSize: 11, overflowWrap: 'anywhere' }}>
              {st.key} → {st.new_ref.split('@')[0]}{st.pinned && <span style={{ color: 'var(--text-muted)' }}> (build {shortSha(st.digest)})</span>}
              <span style={{ color: 'var(--text-muted)', fontFamily: 'inherit' }}> · {st.services} service{st.services !== 1 ? 's' : ''}</span>
            </div>
          ))}
          <div style={{ color: 'var(--text-muted)' }}>
            Found on the registry. A new version can change how it behaves; the first node updates first and rolls back if anything fails to start.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy || working} onClick={() => apply(false)}>
              Change version
            </button>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || working || rolloutActive} onClick={() => apply(true)}
              title={rolloutActive ? 'A roll-out is already running' : 'Change it, then Update Images on every node, the first node first'}>
              Change version and roll out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function EdgeImageRow({ img, busy, rolloutActive, onMove, onSetting, onDone }) {
  const [changing, setChanging] = useState(false);
  const [tag, setTag] = useState(img.track_tag || '');
  const pinned = img.kind === 'digest';
  let status;
  if (!pinned) status = <Tag text="Version tag" tone="#86868b" />;
  else if (img.error && !img.newest) status = <Tag text="Not checked" tone="#86868b" />;
  else if (img.newer) status = <Tag text="Newer build published" tone="#e6b800" />;
  else status = <Tag text="Current" tone="#30d158" />;
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 220px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text)', overflowWrap: 'anywhere' }}>
            {img.key}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {img.services} service{img.services !== 1 ? 's' : ''}
            {pinned && <> · build <span style={{ fontFamily: 'var(--font-mono)' }}>{shortSha(img.digest)}</span></>}
            {img.newer && <> → <span style={{ fontFamily: 'var(--font-mono)' }}>{shortSha(img.newest)}</span>
              {img.newest_first_seen && <>, seen {fmtDt(img.newest_first_seen)}</>}</>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {status}
          {img.newer_versions && img.newer_versions.length > 0 && (
            <Tag text={`${img.newer_versions[0].tag} available`} tone="#0a84ff" />
          )}
          {pinned && (
            <select className="input-sm" value={img.mode} disabled={busy}
              onChange={e => onSetting({ key: img.key, mode: e.target.value })}
              title="Automatic moves the pin and rolls it out once a newer build has been published for the set number of days">
              <option value="manual">Manual</option>
              <option value="auto">Automatic</option>
            </select>
          )}
          {!changing && (
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setChanging(true)}>
              Change version
            </button>
          )}
          {img.newer && (
            <>
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onMove(img.key, false)}>
                Move pin
              </button>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || rolloutActive} onClick={() => onMove(img.key, true)}
                title={rolloutActive ? 'A roll-out is already running' : 'Move the pin, then Update Images on every node, the first node first'}>
                Move pin and roll out
              </button>
            </>
          )}
        </div>
      </div>
      {changing && (
        <ChangeVersionForm img={img} busy={busy} rolloutActive={rolloutActive}
          onCancel={() => setChanging(false)} onDone={(d, text) => { setChanging(false); onDone(d, text); }} />
      )}
      {pinned && !img.tag && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
          Pinned by build only; follows tag
          <input className="input-sm" style={{ width: 110 }} value={tag} placeholder="latest" onChange={e => setTag(e.target.value)} />
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy || tag === (img.track_tag || '')}
            onClick={() => onSetting({ key: img.key, track_tag: tag })}>Save</button>
        </div>
      )}
      {img.mode === 'auto' && img.newer && !img.paused && img.auto_due_at && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Moves automatically from {fmtDt(img.auto_due_at)}.</div>
      )}
      {img.paused && (
        <div style={{ fontSize: 11, color: '#ff2d55' }}>
          Automatic moves paused: {img.paused_reason}. Set the mode again or move the pin by hand to resume.
        </div>
      )}
      {img.error && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Last check: {img.error}</div>
      )}
    </div>
  );
}

function EdgeImagesCard({ isMobile }) {
  const [data, setData]   = useState(null);
  const [open, setOpen]   = useState(false);
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState(null);
  const [days, setDays]   = useState('');

  const load = useCallback(() => {
    authFetch('/api/edge/images').then(r => r.json()).then(d => {
      if (d && !d.error) { setData(d); setDays(String(d.auto_delay_days)); }
      else setMsg({ ok: false, text: (d && d.error) || 'Could not load edge images' });
    }).catch(() => setMsg({ ok: false, text: 'Could not load edge images' }));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const post = async (path, body, okText) => {
    setBusy(true); setMsg(null);
    try {
      const r = await authFetch(`/api/edge/images${path}`, { method: 'POST', body: JSON.stringify(body || {}) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed');
      setData(d); setDays(String(d.auto_delay_days));
      if (okText) setMsg({ ok: true, text: okText });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!data) return msg ? <div className="error-msg" style={{ marginBottom: 16 }}>{msg.text}</div> : null;

  const ro = data.rollout;
  const rolloutActive = !!ro && ['canary', 'fleet'].includes(ro.stage);
  const newer = data.images.filter(i => i.newer);
  const paused = data.images.filter(i => i.paused);
  const notInRepo = data.history.filter(h => !h.in_repo);
  let summary;
  if (rolloutActive) summary = <Tag text={`Rolling out v${ro.target}`} tone="#e6b800" />;
  else if (ro && ro.stage === 'failed') summary = <Tag text="Roll-out stopped" tone="#ff2d55" />;
  else if (newer.length) summary = <Tag text={`${newer.length} newer build${newer.length !== 1 ? 's' : ''}`} tone="#e6b800" />;
  else if (data.behind.length) summary = <Tag text={`${data.behind.length} node${data.behind.length !== 1 ? 's' : ''} behind`} tone="#e6b800" />;
  else summary = <Tag text="All current" tone="#30d158" />;

  return (
    <div className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 16px', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => setOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, color: 'var(--text)' }}>{open ? '▾' : '▸'} Edge images</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>compose.yml v{data.compose_version}</span>
          {summary}
          {paused.length > 0 && <Tag text="Automatic paused" tone="#ff2d55" />}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
          {data.last_check && <span>Updated {fmtDt(data.last_check)}</span>}
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => post('/check', {}, 'Checked every pin')}>
            {busy ? '…' : 'Check now'}
          </button>
        </div>
      </div>

      {msg && (
        <div style={{ padding: '0 16px 10px', fontSize: 12, color: msg.ok ? '#30d158' : '#ff2d55' }}>{msg.text}</div>
      )}

      {open && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          {!data.writable && (
            <div style={{ padding: '10px 16px', fontSize: 12, color: '#ff2d55' }}>
              Clearview can't write edge-dist/compose.yml, so pins can only be moved by editing the file.
            </div>
          )}

          {ro && (
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
                <strong>Roll-out of v{ro.target}</strong>
                <Tag text={(ROLLOUT_STAGE[ro.stage] || {}).label || ro.stage} tone={(ROLLOUT_STAGE[ro.stage] || {}).tone || '#86868b'} />
                <span style={{ color: 'var(--text-muted)' }}>
                  started {fmtDt(ro.started_at)} by {ro.by}{ro.auto ? ' (automatic)' : ''}
                  {ro.finished_at && `, ended ${fmtDt(ro.finished_at)}`}
                </span>
                {rolloutActive && (
                  <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
                    onClick={() => post('/rollout/stop', {}, 'Roll-out stopped; nodes already updating will finish')}>Stop</button>
                )}
              </div>
              {Object.entries(ro.nodes || {}).map(([eid, n]) => (
                <div key={eid} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, flexWrap: 'wrap' }}>
                  <Tag text={(ROLLOUT_NODE[n.status] || {}).label || n.status} tone={(ROLLOUT_NODE[n.status] || {}).tone || '#86868b'} />
                  <span>{n.label}{eid === ro.canary ? ' (first)' : ''}</span>
                  {n.note && <span style={{ color: 'var(--text-muted)' }}>{n.note}</span>}
                </div>
              ))}
            </div>
          )}

          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)' }}>
              {data.behind.length
                ? `${data.behind.length} of ${data.nodes.length} node${data.nodes.length !== 1 ? 's' : ''} not on v${data.compose_version}: ${data.behind.map(n => n.label).join(', ')}`
                : data.nodes.length ? `All ${data.nodes.length} node${data.nodes.length !== 1 ? 's are' : ' is'} on v${data.compose_version}` : 'No edge nodes registered'}
            </span>
            {data.behind.length > 0 && (
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || rolloutActive}
                onClick={() => post('/rollout', {}, 'Roll-out started: first node, then the rest')}>
                Roll out to all nodes
              </button>
            )}
          </div>

          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)' }}>
            <span>Automatic moves after</span>
            <input className="input-sm" type="number" min="0" max="90" style={{ width: 64 }} value={days} onChange={e => setDays(e.target.value)} />
            <span>days</span>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy || days === String(data.auto_delay_days)}
              onClick={() => post('/settings', { auto_delay_days: Number(days) }, 'Saved')}>Save</button>
            <span style={{ marginLeft: isMobile ? 0 : 12 }}>Update first</span>
            <select className="input-sm" value={data.canary_edge_id || ''} disabled={busy}
              onChange={e => post('/settings', { canary_edge_id: e.target.value }, 'Saved')}>
              <option value="">No first node (all at once)</option>
              {data.nodes.map(n => <option key={n.edge_id} value={n.edge_id}>{n.label}</option>)}
            </select>
          </div>

          <div>
            {data.images.map(img => (
              <EdgeImageRow key={img.ref} img={img} busy={busy || !data.writable} rolloutActive={rolloutActive}
                onMove={(key, rollout) => post('/move', { key, rollout }, rollout ? `${key} moved; roll-out started` : `${key} moved; roll out when ready`)}
                onSetting={body => post('/settings', body, 'Saved')}
                onDone={(d, text) => { setData(d); setDays(String(d.auto_delay_days)); setMsg({ ok: true, text }); }} />
            ))}
          </div>

          {data.history.length > 0 && (
            <div style={{ padding: '10px 16px', fontSize: 11, color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <strong style={{ color: 'var(--text)' }}>Recent moves</strong>
                {notInRepo.length > 0 && (
                  <>
                    <span>{notInRepo.length} changed here, not yet in the repo</span>
                    <button type="button" className="btn btn-quiet btn-sm" disabled={busy}
                      onClick={() => post('/settings', { repo_done: true }, 'Marked as in the repo')}>Mark as in the repo</button>
                  </>
                )}
              </div>
              {data.history.map((h, i) => (
                <div key={i}>
                  {fmtDt(h.at)}: {h.kind === 'version' ? <>{h.from} → {h.to}</> : <>{h.key} {shortSha(h.from)} → {shortSha(h.to)}</>}
                  , v{h.version}, by {h.by}{!h.in_repo && ' (not in the repo)'}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EdgeNodes() {
  const isMobile = useIsMobile();
  const [nodes, setNodes]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [selectedId, setSelectedId] = useState(() => new URLSearchParams(window.location.search).get('node') || null);
  const [activeTab, setActiveTab]   = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return ['overview', 'node-health', 'schedule', 'compliance', 'scripts'].includes(t) ? t : 'overview';
  });
  const [clients, setClients]       = useState([]);
  const [refreshMs, setRefreshMsState] = useState(() => {
    try {
      const saved = window.localStorage.getItem(REFRESH_STORAGE_KEY);
      if (saved !== null) return parseInt(saved, 10);
    } catch {}
    return 10000;
  });

  const setRefreshMs = (ms) => {
    setRefreshMsState(ms);
    try { window.localStorage.setItem(REFRESH_STORAGE_KEY, String(ms)); } catch {}
  };

  const load = useCallback((silent) => {
    if (!silent) setLoading(true);
    Promise.all([
      authFetch('/api/edge/nodes').then(r => r.json()),
      authFetch('/api/clients').then(r => r.json()).catch(() => []),
    ])
      .then(([nodesData, clientsData]) => {
        const nodeList = nodesData.nodes || [];
        setNodes(nodeList);
        setClients(Array.isArray(clientsData) ? clientsData : []);
        if (nodeList.length > 0 && (!selectedId || !nodeList.some(n => n.edge_id === selectedId))) setSelectedId(nodeList[0].edge_id);
      })
      .catch(() => setError('Failed to load edge nodes'))
      .finally(() => { if (!silent) setLoading(false); });
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!refreshMs) return;
    const t = setInterval(() => {
      if (activeTab !== 'schedule') load(true);
    }, refreshMs);
    return () => clearInterval(t);
  }, [load, activeTab, refreshMs]);

  // Update label in local state after a rename — no full reload needed
  const handleRenamed = useCallback((edgeId, newLabel) => {
    setNodes(prev => prev.map(n => n.edge_id === edgeId ? { ...n, label: newLabel } : n));
  }, []);

  const selectedNode = nodes.find(n => n.edge_id === selectedId);

  const nodeLabel = (node) => {
    if (node.label) return node.label;
    const client = clients.find(c => c.slug === node.client_slug);
    return client ? client.display_name : node.client_slug;
  };

  const tabs = [
    { id: 'overview',    label: 'Overview' },
    { id: 'node-health', label: 'Node Health' },
    { id: 'schedule',    label: 'Schedule' },
    { id: 'compliance',  label: 'Compliance' },
    { id: 'scripts',     label: 'Scripts' },
  ];

  return (
    <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
      <div className="page-header" style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
        <div>
          <div className="page-title">Clearview Edge Nodes</div>
          <div className="page-subtitle">On-premise security nodes and compliance instances</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
            Auto-refresh
          </label>
          <select className="input-sm"
            value={refreshMs}
            onChange={e => setRefreshMs(Number(e.target.value))}
            style={{ cursor: 'pointer' }}
          >
            {REFRESH_OPTIONS.map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

      <EdgeImagesCard isMobile={isMobile} />

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}><span className="spinner" /></div>
      ) : nodes.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
          No edge nodes registered yet.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: isMobile ? 'stretch' : 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>

          {/* Node list sidebar */}
          <div style={{
            width: isMobile ? '100%' : 220, flexShrink: 0,
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4, padding: '0 4px' }}>
              {nodes.length} Node{nodes.length !== 1 ? 's' : ''}
            </div>
            {nodes.map(node => {
              const status     = nodeStatus(node);
              const color      = STATUS_COLORS[status];
              const isSelected = node.edge_id === selectedId;
              return (
                <button className="btn-card" aria-pressed={isSelected}
                  key={node.edge_id}
                  onClick={() => { setSelectedId(node.edge_id); setActiveTab('overview'); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', width: '100%' }}
                >
                  <StatusDot color={color} pulse={status === 'online'} />
                  <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? 'var(--accent)' : 'var(--text)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {nodeLabel(node)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {node.edge_id.substring(0, 8)}
                      {(status === 'degraded' || status === 'late') && <span style={{ color: '#e6b800', fontSize: 10 }}>Warning</span>}
                      {status === 'offline' && <span style={{ color: '#ff2d55', fontSize: 10 }}>Offline</span>}
                    </div>
                    {(node.nas_ip || node.lan_ip) && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 1 }}>
                        NAS {node.nas_ip || node.lan_ip}
                      </div>
                    )}
                    {siteSummary(node.sites) && (() => {
                      const site = siteSummary(node.sites);
                      return (
                        <div
                          title={site.text}
                          style={{
                            fontSize: 11, marginTop: 2,
                            color: site.named ? 'var(--accent)' : 'var(--text-muted)',
                            fontFamily: site.named ? 'inherit' : 'var(--font-mono)',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}
                        >
                          📍 {site.text}{!site.named && <span style={{ fontFamily: 'inherit' }}> · unnamed</span>}
                        </div>
                      );
                    })()}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          {selectedNode && (
            <div style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : undefined }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, minWidth: 0 }}>
                  <StatusDot color={STATUS_COLORS[nodeStatus(selectedNode)]} pulse={nodeStatus(selectedNode) === 'online'} />
                  <NodeLabelEditor node={selectedNode} onRenamed={handleRenamed} />
                </div>
                <div style={{
                  fontSize: 12, color: 'var(--text-muted)', marginLeft: isMobile ? 0 : 20, marginTop: isMobile ? 4 : 0,
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0,
                }}>
                  <span>{selectedNode.client_slug.replace(/_/g, ' ')}</span>
                  <SiteChips sites={selectedNode.sites} />
                </div>
              </div>

              <div style={{
                display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap',
                gap: 2, marginBottom: 16, borderBottom: '1px solid var(--border)',
              }}>
                {tabs.map(tab => (
                  <button className="tab-btn" aria-selected={activeTab === tab.id} style={{ textAlign: 'center', flexShrink: 0 }} key={tab.id} onClick={() => setActiveTab(tab.id)}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'overview'    && <NodeOverviewTab   node={selectedNode} onRenamed={handleRenamed} onRefresh={() => load(true)} />}
              {activeTab === 'node-health' && <NodeHealthTab     node={selectedNode} />}
              {activeTab === 'schedule'    && <ScheduleTab       node={selectedNode} />}
              {activeTab === 'compliance'  && <NodeComplianceTab node={selectedNode} />}
              {activeTab === 'scripts'     && <ScriptsTab        node={selectedNode} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}